import { forwardRef, useContext, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import t from 'react-translate';
import moment from 'moment';
import { config } from '@config/pendo.config.json';

// Redux
import { useAppDispatch } from 'redux/store';
import { useSelector } from 'react-redux';
import { CompletionStatus, StepKey } from 'redux/schemas/models/mentoring-program-enrollments';
import {
  Cadence,
  MentorshipProgramSession,
  MentorshipProgramMeetingPlan,
  IntegrationAuth,
} from 'redux/schemas/models/mentoring-program-sessions';
import {
  createMentoringProgramSession,
  updateMentoringProgramSession,
  resetMentoringProgramSessionsList,
  fetchMeetingPlan,
} from 'redux/actions/mentoring-program-sessions';
import {
  removeDeletedActionItem,
} from 'redux/actions/mentoring-program-action-items';

// Styles
import { css } from '@emotion/react';
import { gray6, white } from 'styles/global_defaults/colors';
import { quarterSpacing, standardSpacing, threeQuartersSpacing } from 'styles/global_defaults/scaffolding';

// Form
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';
import { getSanitizedStyles } from 'shared/utils';

// Components
import SessionScheduleButtons, { SessionButtonTypes } from './session-schedule-buttons';
import SessionScheduleLocation from './session-schedule-location';
import SessionScheduleRecurrence from './session-schedule-recurrence';
import MentoringProgramContext from 'athena/components/mentoring-program/context';
import SessionPreviewText from './session-preview-text';
import SessionUpdatePreviewText from './session-update-preview-text';

import useConnection from 'athena/hooks/use-connection';
import { DEFAULT_DURATION } from '../plan-session-flyout-modal';
import RecurrenceToggle from './recurrence-toggle';
import ApplyChangesModal, { ApplyChangesOption } from './apply-changes-modal';
import { openConfirmationDialog } from 'redux/actions/confirmation-dialogs';
import { getCadenceWarningModal } from './utils';
import _ from 'lodash';
import { RootState } from 'redux/schemas';
import { AngularContext } from 'react-app';
import NvDropdown, { NvDropdownButtonStyle, NvDropdownOption } from 'shared/components/inputs/nv-dropdown';
import useCalendarIntegration from 'athena/hooks/use-calendar-integration';
import { getStartTime } from 'redux/schemas/models/live-event';

export const convertDateTime = ({
  date,
  timeZone,
  convert = true,
}) => (
  getISOString(
    getStartTime(
      date,
      { id: timeZone },
      convert,
    ))
);

const MOBILE_MAX_WIDTH = 500;

const styles = css`
  .bs4-dropdown {
    .title {
      margin-bottom: 0;
    }
  }

  .session-date-time {
    .session-next-meeting {
      flex: 3;
      .react-datepicker-wrapper {
        width: 100%;
      }
    }
    .session-timezone-duration {
      flex: 2;
    }
    .session-timezone {
      flex: 1;
    }
    .session-duration {
      flex: 1;
      .bs4-dropdown-menu {
        max-height: 210px;
        overflow-y: scroll;
      }
    }
    @media (max-width: ${MOBILE_MAX_WIDTH}px) {
      flex-direction: column;
      .session-next-meeting {
        margin-bottom: ${threeQuartersSpacing}px;
        width: 100%;
        padding-right: 0 !important;
      }
      .session-timezone-duration {
        flex-direction: row;
        width: 100%;
      }
    }
  }

  .cadence {
    .session-cadence {
      flex: 1;
    }
    .session-ends-on {
      flex: 1;
      .react-datepicker-wrapper {
        width: 100%;
      }
    }
    @media (max-width: ${MOBILE_MAX_WIDTH}px) {
      flex-direction: column;
      .session-cadence, .session-ends-on {
        margin-bottom: ${threeQuartersSpacing}px;
        width: 100%;
      }
    }
  }
  .bs4-input-group > input {
    background-color: ${white};
    border-radius: ${quarterSpacing}px;
  }
`;

export const getISOString = (date: string | moment.Moment) => {
  if (!date) return '';
  return moment(date).toISOString();
};

const SESSION_DETAILS_FIELDS = [
  'scheduledDate',
  'title',
  'duration',
  'location',
  'joinUrl',
  'sessionType',
];

const hasBeenModified = (data: any, session: MentorshipProgramSession, fieldsToCheck: string[]) => (
  fieldsToCheck.some((field) => (
    field === 'scheduledDate'
      ? getISOString(data.scheduledDate) !== getISOString(session.scheduledDate)
      : data[field] !== session[field]
  ))
);

type ExtraProps = {
  closureDate: string;
  timezone: string;
  cadence: Cadence;
  endsOn: string | moment.Moment;
};

const getDefaultValues = (
  session: MentorshipProgramSession,
  extraProps: ExtraProps
) => {
  const {
    title,
    scheduledDate,
    duration,
    location = '',
    joinUrl,
    sessionType,
    cadence: sessionCadence,
    endsOn: sessionEndsOn,
  } = session;
  const {
    closureDate,
    timezone,
    cadence,
    endsOn,
  } = extraProps;

  return {
    title,
    scheduledDate: convertDateTime({ date: scheduledDate, timeZone: timezone, convert: false }) || '',
    duration: duration || DEFAULT_DURATION,
    location,
    cadence: sessionCadence || cadence || Cadence.DEFAULT,
    endsOn: getISOString(sessionEndsOn) || getISOString(endsOn) || getISOString(closureDate) || '',
    joinUrl,
    sessionType,
    timezone,
    applyChangesOption: null,
  };
};

type SessionEditModeProps = {
  session: MentorshipProgramSession;
  meetingPlan?: MentorshipProgramMeetingPlan;
  isEdit?: boolean;
  createMeetingPlan?: boolean;
  onSessionCreated: () => void;
  onSessionUpdated: () => void;
  onClose: (shouldCloseModal?: boolean) => void;
  onSubmit: (data: any) => void;
  onFormDirtyChange?: (isDirty: boolean) => void;
  integrations: IntegrationAuth;
  openIntegrationsModal?: () => void;
  deleteSession: () => void;
};

const SessionEditMode = forwardRef((props: SessionEditModeProps, ref) => {
  const {
    session,
    meetingPlan,
    isEdit,
    createMeetingPlan,
    onSessionCreated,
    onSessionUpdated,
    onClose,
    onSubmit: onSessionSubmitted,
    onFormDirtyChange,
    integrations,
    openIntegrationsModal,
    deleteSession,
  } = props;

  const {
    duration,
    mentorshipProgramSessionPlanId,
  } = session;
  const dataQATag = config.pendo.athena.mentorshipProgram.participantHome.sessions.planSessionFlyout;
  const { injectServices } = useContext(AngularContext);
  const [$state] = injectServices(['$state']);
  const { connectionId } = $state.params;

  const { cadence, endDate: endsOn } = meetingPlan || {};
  const hasMeetingPlan = !_.isEmpty(meetingPlan);
  const isPartOfTheMeetingPlan = !!mentorshipProgramSessionPlanId;
  const [submitButtonType, setSubmitButtonType] = useState<SessionButtonTypes>(SessionButtonTypes.SAVE);
  const [showMeetingPlan, setShowMeetingPlan] = useState<boolean>(createMeetingPlan);
  const [cadenceChanged, setCadenceChanged] = useState<boolean>(false);
  const [endDateChanged, setEndDateChanged] = useState<boolean>(false);
  const [shouldShowApplyChangesModal, setShouldShowApplyChangesModal] = useState<boolean>(false);
  const [shouldShowCadenceWarningModal, setShouldShowCadenceWarningModal] = useState<boolean>(false);
  const [shouldValidateBeforeSubmit, setShouldValidateBeforeSubmit] = useState<boolean>(false);
  const [shouldCallSubmit, setShouldCallSubmit] = useState(false);
  const [actionItemsToRemove, setActionItemsToRemove] = useState([]);
  const isSubmitting = useSelector((state: RootState) => (
    state.app.mentoringProgram.sessions?.updateSession?.isLoading
  ));

  const cadenceWarningTranslations = t.MENTORING_PROGRAMS.PARTICIPANT_HOME.PLAN_SESSION_FLYOUT.EDIT_MODE.CADENCE_WARNING_MODAL;

  const { mentoringProgram, timeZone } = useContext(MentoringProgramContext);
  const { endDate: closureDate, mentorshipProgramEnrollment: enrollmentId } = mentoringProgram;

  const { connection, mentorshipProgramEnrollment } = useConnection(enrollmentId);
  const { states: enrollmentStates } = mentorshipProgramEnrollment;
  const { states: connectionStates } = connection;
  const states = useMemo(
      () => ({ ...enrollmentStates, ...connectionStates }),
      [enrollmentStates, connectionStates]
    );

  const customStyles = createMeetingPlan ? css`
    .location-info {
      border-bottom: 1px solid ${gray6};
      padding-bottom: ${standardSpacing}px;
    }
  ` : '';

  const translationBase = t.MENTORING_PROGRAMS.PARTICIPANT_HOME.PLAN_SESSION_FLYOUT.EDIT_MODE;

  const validationSchema = yup.object().shape({
    title: yup.string().required(t.VALIDATION.IS_A_REQUIRED_FIELD(translationBase.TITLE())),
    scheduledDate: yup.string().required(t.VALIDATION.IS_A_REQUIRED_FIELD(translationBase.DATE_AND_TIME())),
    duration: yup.number(),
    location: yup.string().optional().nullable().max(190),
    cadence: yup.string().optional().nullable(),
    endsOn: yup.string().optional().nullable(),
    joinUrl: yup.string().url().optional().nullable(),
    sessionType: yup.string().optional().nullable(),
    timezone: yup.string().optional().nullable(),
    applyChangesOption: yup.string().optional().nullable(),
  });

  const shouldPickClosureDate = createMeetingPlan && !isEdit;

  const extraProps = {
    closureDate: shouldPickClosureDate ? closureDate : null,
    timezone: timeZone.ianaTimeZone,
    cadence: (isPartOfTheMeetingPlan || createMeetingPlan) ? (cadence || Cadence.WEEKLY) : Cadence.DEFAULT,
    endsOn: (isPartOfTheMeetingPlan || showMeetingPlan) ? endsOn : '',
  };

  const defaultValues = getDefaultValues(session, extraProps);

  const methods = useForm({
    mode: 'all',
    defaultValues,
    resolver: yupResolver(validationSchema),
  });

  const { handleSubmit, formState, setValue, getValues } = methods;
  const { isDirty } = formState;
  const cadenceFormValue = methods.watch('cadence');
  const scheduledDateFormValue = methods.watch('scheduledDate');
  const endsOnFormValue = methods.watch('endsOn');
  const isOptional = !hasMeetingPlan && !isEdit && !!scheduledDateFormValue && !createMeetingPlan;

  const dispatch = useAppDispatch();

  const formatSessionData = (data) => ({
    ...data,
    scheduledDate: convertDateTime({ date: data.scheduledDate, timeZone: timeZone.ianaTimeZone }),
    programId: mentoringProgram.id,
    enrollmentId,
    mentorshipProgramConnectionId: connection.connectionId,
    cadence: createMeetingPlan ? (cadenceFormValue || Cadence.WEEKLY) : cadenceFormValue,
    createMeetingPlan: createMeetingPlan || (isOptional && !hasMeetingPlan && cadenceFormValue),
  });

  const shouldShowRecurrenceToggle = hasMeetingPlan && isPartOfTheMeetingPlan && isEdit;
  const shouldShowPreviewText = !isEdit && (
    createMeetingPlan || (showMeetingPlan && _.isEmpty(meetingPlan))
  );
  const shouldShowUpdatePreviewText = (
    (isEdit && hasMeetingPlan && isPartOfTheMeetingPlan && endsOnFormValue)
    && (cadenceChanged || endDateChanged)
  );

  const handleSuccess = (action, callback) => {
    dispatch(resetMentoringProgramSessionsList({ resetList: true }));
    if (isPartOfTheMeetingPlan || (cadenceFormValue && endsOnFormValue)) {
      dispatch(fetchMeetingPlan({
        programId: mentoringProgram.id,
        mentorshipProgramConnectionId: connectionId,
      }));
    }
    callback?.();
    onSessionSubmitted(action.payload?.[0]);
    if (actionItemsToRemove?.length) {
      actionItemsToRemove.forEach((actionItem) => {
        dispatch(removeDeletedActionItem({ connectionId, actionItemId: actionItem.id }));
      });
    }
  };

  const onSubmit = (data) => {
    if (shouldValidateBeforeSubmit) {
      // We have 3 cases here:
      // 1. If the user hasn't changed the cadence, we should show the apply changes modal
      // 2. If the user has changed the cadence, we should show a warning modal
      // 3. If the user has changed the cadence or endsOn and one of the other fields,
      //    we should submit the form by setting the applyChangesOption inside data to ApplyChangesOption.REMAINING_SESSIONS
      if (submitButtonType !== SessionButtonTypes.UPDATE_ALL) {
        if (!cadenceChanged) {
          setShouldShowApplyChangesModal(true);
          return;
        }
        const cadenceOrEndDateChanged = cadenceChanged || endDateChanged;
        const otherFieldsChanged = hasBeenModified(data, session, SESSION_DETAILS_FIELDS);
        if (!cadenceOrEndDateChanged || !otherFieldsChanged) {
          setShouldShowCadenceWarningModal(true);
          return;
        }
      }
    }

    const sessionData = formatSessionData(data);
    sessionData.endsOn = getISOString(sessionData.endsOn);
    sessionData.applyChangesOption = submitButtonType === SessionButtonTypes.UPDATE_ALL
      ? ApplyChangesOption.REMAINING_SESSIONS
      : data.applyChangesOption;

    if (isEdit) {
      dispatch(updateMentoringProgramSession({ ...sessionData, sessionId: session.id }))
        .then((action) => handleSuccess(action, onSessionUpdated));
    } else {
      dispatch(createMentoringProgramSession(sessionData))
        .then((action) => handleSuccess(action, onSessionCreated));
    }
  };

  useEffect(() => {
    if (isSubmitting || formState.isSubmitting) {
      switch (submitButtonType) {
        case SessionButtonTypes.ADD:
          setSubmitButtonType(SessionButtonTypes.ADDING);
          break;
        case SessionButtonTypes.SAVE:
          setSubmitButtonType(SessionButtonTypes.SAVING);
          break;
        case SessionButtonTypes.CREATE_SESSIONS:
          setSubmitButtonType(SessionButtonTypes.CREATING_SESSIONS);
          break;
        case SessionButtonTypes.UPDATE_ALL:
        case SessionButtonTypes.UPDATE_ALL_REMAINING_SESSIONS:
          setSubmitButtonType(SessionButtonTypes.UPDATING);
      }
    }
  }, [isSubmitting, formState.isSubmitting, submitButtonType]);

  const shouldDisableSubmitButton = () => (
    formState.isSubmitting
    || !formState.isValid
    || !isDirty
    || (cadenceFormValue && !endsOnFormValue)
    || (endsOnFormValue && !cadenceFormValue)
    || isSubmitting
  );

  const onApplyChanges = (option: ApplyChangesOption) => {
    setShouldShowApplyChangesModal(false);
    setShouldValidateBeforeSubmit(false);

    if (option === ApplyChangesOption.REMAINING_SESSIONS) {
      setShouldShowCadenceWarningModal(true);
      return;
    }

    setShouldCallSubmit(true);
  };

  useEffect(() => {
    if (isSubmitting || formState.isSubmitting) return;
    if (isEdit) {
      if (cadenceChanged && isPartOfTheMeetingPlan) {
        setSubmitButtonType(SessionButtonTypes.UPDATE_ALL_REMAINING_SESSIONS);
        return;
      } else {
        const hasFieldsBecomeModified = hasBeenModified(methods.getValues(), session, SESSION_DETAILS_FIELDS);
        setSubmitButtonType(
          endDateChanged && !hasFieldsBecomeModified && isPartOfTheMeetingPlan
          ? SessionButtonTypes.UPDATE_ALL
          : SessionButtonTypes.SAVE
        );
        return;
      }
    }
    setSubmitButtonType(createMeetingPlan
      ? SessionButtonTypes.CREATE_SESSIONS
      : SessionButtonTypes.ADD
    );
  }, [cadenceChanged, endDateChanged, isSubmitting, formState.isSubmitting, createMeetingPlan, isEdit, methods.getValues()]);

  useEffect(() => {
    if (isOptional) {
      const hasFinishedAllSteps = states[StepKey.WAITING_FIRST_SESSION]?.status === CompletionStatus.COMPLETED;
      // Scheduled date must be future for a meeting plan
      const isScheduledDateValid = scheduledDateFormValue && moment(scheduledDateFormValue).isAfter(moment());
      setShowMeetingPlan(hasFinishedAllSteps && isScheduledDateValid);
    }
  }, [isOptional, scheduledDateFormValue]);

  useEffect(() => {
    onFormDirtyChange?.(isDirty);
  }, [isDirty]);

  useEffect(() => {
    setCadenceChanged(cadence !== cadenceFormValue);
  }, [cadenceFormValue]);

  useEffect(() => {
    if (getISOString(endsOn) !== getISOString(endsOnFormValue)) {
      setEndDateChanged(true);
    } else {
      setEndDateChanged(false);
    }
  }, [endsOnFormValue]);

  useEffect(() => {
    if (shouldShowCadenceWarningModal) {
      const sessions = meetingPlan?.mentorshipProgramSessions.filter(({ mentorshipProgramActionItems, details }) => (
        mentorshipProgramActionItems.length || details
      ));
      if (sessions.length === 0) {
        // We need to update the value of applyChangesOption to ApplyChangesOption.REMAINING_SESSIONS
        setValue('applyChangesOption', ApplyChangesOption.REMAINING_SESSIONS);
        setShouldValidateBeforeSubmit(false);
        setShouldCallSubmit(true);
        return;
      }
      const cadenceWarningModal = getCadenceWarningModal({
        translations: cadenceWarningTranslations,
        sessions,
        onCancel: () => setShouldShowCadenceWarningModal(false),
        onConfirm: () => {
          const actionItems = sessions.map((session) => session.mentorshipProgramActionItems);
          setShouldShowCadenceWarningModal(false);
          setValue('applyChangesOption', ApplyChangesOption.REMAINING_SESSIONS);
          setShouldValidateBeforeSubmit(false);
          setShouldCallSubmit(true);
          setActionItemsToRemove(_.flattenDeep(actionItems));
        }
      });
      dispatch(openConfirmationDialog(cadenceWarningModal));
    }
  }, [shouldShowCadenceWarningModal]);

  useEffect(() => {
    setShouldValidateBeforeSubmit(isEdit && hasMeetingPlan && isPartOfTheMeetingPlan);
  }, [isEdit, hasMeetingPlan, mentorshipProgramSessionPlanId]);

  useEffect(() => {
    if (shouldCallSubmit) {
      handleSubmit(onSubmit)();
      setShouldCallSubmit(false);
    }
  }, [shouldCallSubmit]);

  useEffect(() => {
    if (shouldPickClosureDate && !methods.getValues('endsOn')) {
      setValue('endsOn', getISOString(closureDate));
    }
  }, [shouldPickClosureDate]);

  useImperativeHandle(
    ref,
    () => ({ getSession: getValues }),
    [getValues]
  );

  const deleteSessionOption: NvDropdownOption = {
    type: 'text',
    class: 'text-danger',
    text: t.MENTORING_PROGRAMS.PARTICIPANT_HOME.PLAN_SESSION_FLYOUT.OPTIONS.DELETE_SESSION(),
    callback: deleteSession,
  };

  const configureIntegrationsOption: NvDropdownOption = {
    type: 'text',
    text: t.MENTORING_PROGRAMS.PARTICIPANT_HOME.PLAN_SESSION_FLYOUT.OPTIONS.CONFIGURE_INTEGRATIONS(),
    callback: openIntegrationsModal,
  };

  const optionItems = [];

  const { hasMs365CalendarIntegration } = useCalendarIntegration();
  const hasIntegrationsEnabled = integrations?.source?.length > 0 || hasMs365CalendarIntegration;

  if (hasIntegrationsEnabled) {
    optionItems.push(configureIntegrationsOption);
  }

  if (isEdit) {
    optionItems.push(deleteSessionOption);
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className='header'>
          <div>
            {!isEdit && createMeetingPlan
              ? t.MENTORING_PROGRAMS.PARTICIPANT_HOME.PLAN_SESSION_FLYOUT.EDIT_MODE.HEADER()
              : t.MENTORING_PROGRAMS.PARTICIPANT_HOME.PLAN_SESSION_FLYOUT.HEADER()}
          </div>
          {optionItems.length > 0 && (
            <div className='d-flex'>
              <NvDropdown
                buttonStyle={NvDropdownButtonStyle.ICON}
                iconClass='icon-more icon-small mr-6 ml-4 text-gray-2'
                items={optionItems}
                toggleDataQa={dataQATag.sessionMenuDropdown}
              />
            </div>
          )}
        </div>
        <div className='content' css={getSanitizedStyles([styles, customStyles])}>
          {isEdit && (
            <div className='heading-4 pb-4 pt-4'>
              {t.MENTORING_PROGRAMS.PARTICIPANT_HOME.PLAN_SESSION_FLYOUT.EDIT_MODE.SUBTITLE()}
            </div>
          )}
          {!isEdit && createMeetingPlan && (
            <div className='text-large-body pb-4 gray-1'>
              {t.MENTORING_PROGRAMS.PARTICIPANT_HOME.PLAN_SESSION_FLYOUT.EDIT_MODE.DESCRIPTION()}
            </div>
          )}
          <SessionScheduleLocation
            id={session?.id}
            integrations={integrations}
            isEdit={isEdit}
            openIntegrationsModal={openIntegrationsModal}
          />
          <SessionScheduleRecurrence
            duration={duration}
            isOptional={isOptional}
            showMeetingPlan={showMeetingPlan && _.isEmpty(meetingPlan)}
            allowPastDateSelection={!shouldShowRecurrenceToggle}
          />
          {shouldShowRecurrenceToggle && (
            <RecurrenceToggle />
          )}
          {shouldShowPreviewText && (
            <SessionPreviewText
              scheduledDate={scheduledDateFormValue}
              cadence={cadenceFormValue}
              endsOn={endsOnFormValue}
              closureDate={closureDate}
            />
          )}
          {shouldShowUpdatePreviewText && (
            <SessionUpdatePreviewText
              scheduledDate={scheduledDateFormValue}
              cadence={cadenceFormValue}
              endsOn={endsOnFormValue}
              closureDate={closureDate}
              cadenceChanged={cadenceChanged}
              endDateChanged={endDateChanged}
            />
          )}
        </div>
        <SessionScheduleButtons
          type={submitButtonType}
          onCancel={() => onClose(false)}
          disabled={shouldDisableSubmitButton()}
        />
      </form>
      <ApplyChangesModal
        show={shouldShowApplyChangesModal}
        onClose={() => setShouldShowApplyChangesModal(false)}
        onSave={onApplyChanges}
      />
    </FormProvider>
  );
});

export default SessionEditMode;
